import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Switch, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import MaterialSymbol from '../common/MaterialSymbol';
import platformSettingsService, { fieldErrors } from '../../services/platformSettingsService';
import type { PlatformJob, PlatformSettings } from '../../types';

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleString('de-DE') : '');

const StatusChip: React.FC<{ job: PlatformJob }> = ({ job }) => {
    const translate = useTranslate();
    if (!job.status) return <Chip size="small" variant="outlined" label={translate('platformSettings.jobs.neverRun')} />;
    const icon = job.status === 'ok' ? 'check_circle' : job.status === 'failed' ? 'error' : 'sync';
    return (
        <Chip
            size="small"
            variant="outlined"
            data-testid={`job-status-${job.key}`}
            icon={<MaterialSymbol name={icon} size={16} />}
            label={translate(`platformSettings.jobs.status.${job.status}`)}
        />
    );
};

/**
 * Wiederkehrende Läufe (Symfony Scheduler): aktiv/inaktiv, Cron-Override (leer = Standard
 * im Code) und letzte Ausführung mit Ergebnis.
 */
const JobsTab: React.FC<{ data: PlatformSettings; onChange: (s: PlatformSettings) => void }> = ({ data, onChange }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [form, setForm] = useState<Record<string, { enabled: boolean; cron: string }>>(() =>
        Object.fromEntries(data.jobs.map((j) => [j.key, { enabled: j.enabled, cron: j.cronIsDefault ? '' : j.cron }])));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        setErrors({});
        try {
            const jobs = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, { enabled: v.enabled, ...(v.cron.trim() ? { cron: v.cron.trim() } : {}) }]));
            onChange(await platformSettingsService.update({ jobs }));
            notify(translate('platformSettings.saved'), { type: 'success' });
        } catch (err: any) {
            const fe = fieldErrors(err);
            setErrors(fe);
            notify(translate(Object.keys(fe).length ? 'platformSettings.invalid' : 'platformSettings.saveError'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>{translate('platformSettings.jobs.intro')}</Alert>
            <Box sx={{ display: 'grid', gap: 2 }}>
                {data.jobs.map((job) => {
                    const state = form[job.key];
                    const error = errors[`jobs.${job.key}.cron`];
                    return (
                        <Paper key={job.key} sx={{ p: 2 }} data-testid={`job-${job.key}`}>
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.4fr 1fr 1.4fr' }, alignItems: 'start' }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{job.label}</Typography>
                                    <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{job.description}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Switch
                                            checked={state.enabled}
                                            onChange={(e) => setForm((p) => ({ ...p, [job.key]: { ...p[job.key], enabled: e.target.checked } }))}
                                            slotProps={{ input: { 'aria-label': `${job.label}: ${translate('platformSettings.jobs.enabled')}`, 'data-testid': `job-enabled-${job.key}` } as React.InputHTMLAttributes<HTMLInputElement> }}
                                        />
                                        <Typography variant="body2">{translate(state.enabled ? 'platformSettings.jobs.enabled' : 'platformSettings.jobs.disabled')}</Typography>
                                    </Box>
                                </Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label={translate('platformSettings.jobs.cron')}
                                    value={state.cron}
                                    placeholder={job.defaultCron}
                                    onChange={(e) => setForm((p) => ({ ...p, [job.key]: { ...p[job.key], cron: e.target.value } }))}
                                    error={!!error}
                                    helperText={error ?? translate('platformSettings.jobs.cronDefault', { cron: job.defaultCron })}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': `job-cron-${job.key}` } }}
                                />
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 0.5 }}>{translate('platformSettings.jobs.lastRun')}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <StatusChip job={job} />
                                        {job.lastRunAt && <Typography variant="body2">{formatDate(job.lastRunAt)}</Typography>}
                                    </Box>
                                    {job.message && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--oa-text-2)', wordBreak: 'break-word' }} data-testid={`job-message-${job.key}`}>
                                            {job.message}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
            <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={save} disabled={saving} data-testid="save-jobs">{translate('platformSettings.save')}</Button>
            </Box>
        </Box>
    );
};

export default JobsTab;
